/**
 * 景點編輯的「基本資料」分頁：識別碼、名稱、描述、座標。
 *
 * 這些欄位跟窗屏無關（`description` 是給網站與購票機的 HTML 文案），與其他三個
 * 分頁的內容性質完全不同——把它們分開，編輯窗屏內容的人就不必每次捲過一大段
 * 所見即所得編輯器。
 */

import { RichTextEditor } from './RichTextEditor';
import { FIELD_MAX_WIDTH, LOCALE_LABEL, type Locale, type SpotFormState } from './spot-form';

interface Props {
  form: SpotFormState;
  setForm: (next: SpotFormState | ((prev: SpotFormState) => SpotFormState)) => void;
  activeLocale: Locale;
  isEdit: boolean;
}

export function SpotBasicFields({ form, setForm, activeLocale, isEdit }: Props): JSX.Element {
  /** 解析「緯度, 經度」貼上字串 → 填入 lat/lng。 */
  const parseCoordPaste = (raw: string): void => {
    const m = raw.match(/(-?\d+(?:\.\d+)?)\s*[,\s]\s*(-?\d+(?:\.\d+)?)/);
    if (m) setForm((p) => ({ ...p, latitude: m[1]!, longitude: m[2]! }));
  };

  const latNum = Number(form.latitude);
  const lngNum = Number(form.longitude);
  const coordsValid =
    !Number.isNaN(latNum) && !Number.isNaN(lngNum) && form.latitude !== '' && form.longitude !== '';

  return (
    <>
      <Field label="景點代碼 spot_code">
        <input
          type="text"
          value={form.spot_code}
          disabled={isEdit}
          onChange={(e) => setForm({ ...form, spot_code: e.target.value })}
          placeholder="例：SPOT-PIER-01"
          style={{ width: '100%', fontFamily: 'monospace', padding: 8 }}
        />
        {isEdit && <small style={{ color: '#9ca3af' }}>spot_code 建立後不可變更</small>}
      </Field>

      <Field label="窗屏景點編號 roi_id">
        <input
          type="number"
          min={0}
          step={1}
          value={form.roi_id}
          onChange={(e) => setForm({ ...form, roi_id: e.target.value })}
          placeholder="例：3（留空代表此景點不納入窗屏導覽）"
          style={{ width: 260, padding: 8, fontFamily: 'monospace', display: 'block' }}
        />
        <small style={{ color: '#9ca3af', display: 'block', marginTop: 3 }}>
          決定船上檔案路徑 <code>assets/{form.roi_id.trim() || 'N'}/</code>，也是
          ATTRACTION_LOCATION.TXT / ATTRACTION_NANE.TXT 的第一欄。同一編號不可重複。
        </small>
      </Field>

      <Field label={`名稱（${LOCALE_LABEL[activeLocale]}）`}>
        <input
          type="text"
          value={form.name[activeLocale]}
          onChange={(e) =>
            setForm({ ...form, name: { ...form.name, [activeLocale]: e.target.value } })
          }
          placeholder={activeLocale === 'zh-TW' ? '例：安平古堡' : ''}
          style={{ width: '100%', padding: 8 }}
        />
        <small style={{ color: '#9ca3af' }}>名稱至少需填一種語言</small>
      </Field>

      <Field label={`描述（${LOCALE_LABEL[activeLocale]}）`}>
        <RichTextEditor
          key={activeLocale}
          value={form.description[activeLocale]}
          onChange={(html) =>
            setForm((p) => ({ ...p, description: { ...p.description, [activeLocale]: html } }))
          }
          placeholder="輸入景點介紹⋯可用上方工具列做粗體 / 清單 / 連結等排版"
        />
        <small style={{ color: '#9ca3af' }}>
          供網站與購票機顯示；<strong>不會</strong>送到船上窗屏（窗屏內容請切到上方的畫面分頁）。
        </small>
      </Field>

      <fieldset
        style={{
          border: '1px solid #e5e7eb',
          borderRadius: 6,
          padding: 12,
          margin: '12px 0',
          // 跟著裡面的欄位一起收；框線拉滿整個寬螢幕而內容只有一半，看起來像壞掉
          maxWidth: FIELD_MAX_WIDTH,
        }}
      >
        <legend style={{ fontSize: 13, fontWeight: 600 }}>位置座標</legend>
        <Field label="快速貼上座標">
          <input
            type="text"
            onChange={(e) => parseCoordPaste(e.target.value)}
            placeholder="貼上「緯度, 經度」，例：23.0010, 120.1610（自動拆成下方兩欄）"
            style={{ width: '100%', padding: 8 }}
          />
          <small style={{ color: '#9ca3af' }}>
            於 Google Maps 對景點按右鍵可複製「緯度, 經度」，直接貼到這裡
          </small>
        </Field>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <Field label="緯度 latitude">
            <input
              type="number"
              step="any"
              value={form.latitude}
              onChange={(e) => setForm({ ...form, latitude: e.target.value })}
              style={{ width: 150, padding: 8 }}
            />
          </Field>
          <Field label="經度 longitude">
            <input
              type="number"
              step="any"
              value={form.longitude}
              onChange={(e) => setForm({ ...form, longitude: e.target.value })}
              style={{ width: 150, padding: 8 }}
            />
          </Field>
          <Field label="觸發半徑 (m)">
            <input
              type="number"
              value={form.trigger_radius_m}
              onChange={(e) => setForm({ ...form, trigger_radius_m: e.target.value })}
              style={{ width: 110, padding: 8 }}
            />
          </Field>
          {coordsValid && (
            <a
              href={`https://www.google.com/maps?q=${latNum},${lngNum}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 13, color: '#2563eb', paddingBottom: 14 }}
            >
              在 Google Maps 開啟確認 ↗
            </a>
          )}
        </div>
      </fieldset>
    </>
  );
}

export function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}): JSX.Element {
  return (
    <div style={{ marginBottom: 10, maxWidth: FIELD_MAX_WIDTH }}>
      <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
        {label}
      </label>
      {children}
    </div>
  );
}
